import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { IErrorResponse } from "sharedFeatures/sharedServices/Responses";
import {IPlanTeamCustomers} from "sharedFeatures/sharedServices/Responses/SharedPlan/IPlanTeamCustomers";
import {IGrantRights} from "sharedFeatures/sharedServices/Responses/SharedPlan/IGrantRights";
import {IDenyCustomer} from "sharedFeatures/sharedServices/Responses/SharedPlan/IDenyCustomer";
import {ISendSharedPlanInvitation} from "sharedFeatures/sharedServices/Responses/SharedPlan/ISendSharedPlanInvitation";
import {ISetPlanLastViewBulk} from "sharedFeatures/sharedServices/Responses/SharedPlan/ISetPlanLastViewBulk";
import {ISetPlanLastView} from "sharedFeatures/sharedServices/Responses/SharedPlan/ISetPlanLastView";
import { DashboardGridItemView } from 'sharedFeatures/sharedModels';

declare let handleAngularHttpError: any;

@Injectable()
export class SharedPlanService {

    constructor(private http: HttpClient) { }

    // Grant rights on the plan to the customer
    public GrantRights(planGuid: string, email: string, rights: Array<string>) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        var data = { planGuid, email, rights };

        // Wrap http response with the custom promise to handle error
            const response = this.http.post<string>(window.appPath + 'SharedPlan/GrantRights', data, { headers: headers })
            return lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | IGrantRights = { success: false, errorMessage: '' };

                    if (res && res.length && res.indexOf("error=sessiontimeout") === -1)
                        data = JSON.parse(res);

                    return data;
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GrantRights error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    throw error;
                });
    }

    // Revoke the plan sharing from the customer
    public DenyCustomer(planGuid: string, email: string) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        var data = { planGuid, email };

        // Wrap http response with the custom promise to handle error
        const response = this.http.post<string>(window.appPath + 'SharedPlan/DenyCustomer', data, { headers: headers });
        return lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | IDenyCustomer = { success: false, errorMessage: '' };

                    if (res && res.length && res.indexOf("error=sessiontimeout") === -1)
                        data = JSON.parse(res);

                    return data;
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('DenyCustomer error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    throw error;
                });
    }

    public getSharedTeamUsers(planGUID: string): Promise<IPlanTeamCustomers | IErrorResponse> {


        let headers = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');

        let params = new HttpParams()
            .set('planGUID', planGUID);
        let body = params.toString();

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'SharedPlan/GetPlanTeamCustomers', body, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IPlanTeamCustomers | IErrorResponse = { success: false, errorMessage: '' };

                    if (res && res.length)
                        data = JSON.parse(res);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetSharedTeamUsers error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });


    }

    // Send email with invitation to the shared plan
    public SendSharedPlanInvitation(email: string, planName: string, ownerEmail: string, planUrl: string, companyGuid: string, addToTeam: boolean = false) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let data = { email, planName, ownerEmail, planUrl, companyGuid, addToTeam };

        // Wrap http response with the custom promise to handle error
        const response = this.http.post<string>(window.appPath + 'SharedPlan/SendSharedPlanInvitation', data, { headers: headers });
        return lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | ISendSharedPlanInvitation = { success: false, errorMessage: '' };

                    if (res && res.length)
                    {
                        if (res.indexOf("error=sessiontimeout") === -1)
                            data = JSON.parse(res);
                    }

                    return data;
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('SendSharedPlanInvitation error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    throw error;
                });
    }

    // Store last view of plan
    public async SetPlanLastView<T = any>(
        planGUID: string,
        userId: number,
        userEmail: string,
        key: string,
        value: T) {
        
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const data = { planGUID, userId, userEmail, key, value };
        
        const response = await this.http.post<string>(window.appPath + 'SharedPlan/SetPlanLastView', data, { headers: headers });
        
        return lastValueFrom(response)
            .then((res: string) => {
                let data: IErrorResponse | ISetPlanLastView = { success: false, errorMessage: '' };

                if (res && res.length)
                {
                    if (res.indexOf("error=sessiontimeout") === -1)
                        data = JSON.parse(res);
                }

                return data;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('SetPlanLastView error', error);

                handleAngularHttpError(error);

                //if we need to transmit the error to a component
                throw error;
            });
    }

    // Store last view of plan
    public async SetDashboardItems(
        planGUID: string,
        userId: number,
        userEmail: string,        
        value: DashboardGridItemView[]) {

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const data = { planGUID, userId, userEmail, value };

        const response = await this.http.post<string>(window.appPath + 'SharedPlan/SetDashboardItems', data, { headers: headers });

        return lastValueFrom(response)
            .then((res: string) => {
                let data: IErrorResponse | ISetPlanLastView = { success: false, errorMessage: '' };

                if (res && res.length) {
                    if (res.indexOf("error=sessiontimeout") === -1)
                        data = JSON.parse(res);
                }

                return data;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('SetDashboardItems error', error);

                handleAngularHttpError(error);

                //if we need to transmit the error to a component
                throw error;
            });
    }

    public async SetPlanLastViewBulk(
        planGUID: string,
        userId: number,
        userEmail: string,
        changes: any) {
        
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const data = { planGUID, userId, userEmail, changes };
        
        const response = await this.http.post<string>(window.appPath + 'SharedPlan/SetPlanLastViewBulk', data, { headers: headers });

        return lastValueFrom(response)
            .then((res: string) => {
                let data: IErrorResponse | ISetPlanLastViewBulk = { success: false, errorMessage: '' };

                if (res && res.length)
                {
                    if (res.indexOf("error=sessiontimeout") === -1)
                        data = JSON.parse(res);
                }

                return data;
            })
            .catch((error: HttpErrorResponse) => {
                console.error('SetPlanLastViewBulk error', error);

                handleAngularHttpError(error);

                //if we need to transmit the error to a component
                throw error;
            });
    }
}
